import {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Icon from '../../../../components/common/Icon/Icon';
import { IconKeys } from '../../../../components/common/Icon/IconKeys';
import S from './BroadcastQueueStyles';

function PlayIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
      <path d="M3 2v10l8-5-8-5z" />
    </svg>
  );
}

function PauseIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
      <rect x="2" y="2" width="4" height="10" rx="1" />
      <rect x="8" y="2" width="4" height="10" rx="1" />
    </svg>
  );
}

type Props = {
  filename: string;
  summary: string;
  isExpanded: boolean;
  onToggle: () => void;
  searchValue: string;
  onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSearchSubmit: (query: string) => void;
  onClearSearch: () => void;
  isSearchActive: boolean;
  isSearchLoading?: boolean;
  onAbort: () => void;
  abortCount?: number;
  abortDisabled?: boolean;
  audioUrl?: string | null;
  itemId: string;
  playingId: string | null;
  onPlayPause: (itemId: string, isPlaying: boolean) => void;
};

export default function BroadcastQueueItemHeader({
  filename,
  summary,
  isExpanded,
  onToggle,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  onClearSearch,
  isSearchActive,
  isSearchLoading = false,
  onAbort,
  abortCount = 0,
  abortDisabled = false,
  audioUrl,
  itemId,
  playingId,
  onPlayPause,
}: Props): JSX.Element {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const playbackUrlRef = useRef<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (isPlaying) return;
    playbackUrlRef.current = null;
    setIsPlaying(false);
  }, [audioUrl, isPlaying]);

  useEffect(() => {
    if (playingId != null && playingId !== itemId) {
      if (audioRef.current) audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [playingId, itemId]);

  useEffect(() => {
    if (playingId === itemId) {
      setIsPlaying(true);
    }
  }, [playingId, itemId]);

  const handleEnded = useCallback((): void => {
    setIsPlaying(false);
    onPlayPause(itemId, false);
  }, [itemId, onPlayPause]);

  const handleError = useCallback((): void => {
    setIsPlaying(false);
    onPlayPause(itemId, false);
  }, [itemId, onPlayPause]);

  const getSearchQueryFromInput = useCallback((): string => {
    const fromRef = searchInputRef.current?.value;
    if (fromRef != null && fromRef !== '') {
      return String(fromRef).trim();
    }
    return String(searchValue).trim();
  }, [searchValue]);

  const handleSearchSubmit = useCallback((): void => {
    const query = getSearchQueryFromInput();
    if (query.length > 0) {
      onSearchSubmit(query);
    }
  }, [getSearchQueryFromInput, onSearchSubmit]);

  const handlePlayPause = useCallback((): void => {
    if (!audioUrl || !audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      onPlayPause(itemId, false);
    } else {
      playbackUrlRef.current = audioUrl;
      setIsPlaying(true);
      onPlayPause(itemId, true);
      audioRef.current.play().catch(() => {
        setIsPlaying(false);
        onPlayPause(itemId, false);
      });
    }
  }, [audioUrl, itemId, isPlaying, onPlayPause]);

  const abortLabel = abortCount > 0 ? `Abort (${abortCount} devices)` : 'Abort';

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearchSubmit();
      }
    },
    [handleSearchSubmit],
  );

  return (
    <S.ItemHeader>
      <S.HeaderLeft>
        {audioUrl ? (
          <S.PlayButton
            type="button"
            onClick={(e): void => {
              e.stopPropagation();
              handlePlayPause();
            }}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </S.PlayButton>
        ) : (
          <S.PlayIconWrap aria-hidden>
            <PlayIcon />
          </S.PlayIconWrap>
        )}
        {audioUrl && (
          <audio
            ref={audioRef}
            src={isPlaying && playbackUrlRef.current ? playbackUrlRef.current : audioUrl}
            onEnded={handleEnded}
            onError={handleError}
            style={{ display: 'none' }}
          >
            <track kind="captions" src="" label="English" />
          </audio>
        )}
        <S.HeaderTitleBlock>
          <S.HeaderFilename title={filename}>{filename}</S.HeaderFilename>
          <S.HeaderSummary>{summary}</S.HeaderSummary>
        </S.HeaderTitleBlock>
      </S.HeaderLeft>
      <S.HeaderRight onClick={(e): void => e.stopPropagation()}>
        <S.SearchWrap>
          <S.SearchInputWrap>
            <S.SearchIconButton
              type="button"
              onClick={(e): void => {
                e.preventDefault();
                e.stopPropagation();
                handleSearchSubmit();
              }}
              disabled={isSearchActive && isSearchLoading}
              aria-label="Search"
            >
              <Icon iconKey={IconKeys.SEARCH} />
            </S.SearchIconButton>
            <input
              ref={searchInputRef}
              type="text"
              name="search-device"
              placeholder="Search device"
              value={searchValue}
              onChange={onSearchChange}
              onKeyDown={handleSearchKeyDown}
              aria-label="Search device"
              autoComplete="off"
            />
            {isSearchActive && (
              <S.SearchClearButton
                type="button"
                onClick={onClearSearch}
                aria-label="Clear search"
              >
                <Icon iconKey={IconKeys.CLOSE} />
              </S.SearchClearButton>
            )}
          </S.SearchInputWrap>
        </S.SearchWrap>
        <S.AbortButton type="button" onClick={onAbort} aria-label={abortLabel} disabled={abortDisabled}>
          <Icon iconKey={IconKeys.CLOSE} />
          {abortLabel}
        </S.AbortButton>
        <S.ExpandButton
          type="button"
          $expanded={isExpanded}
          onClick={onToggle}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          aria-expanded={isExpanded}
        >
          <Icon iconKey={IconKeys.CHEVRON_RIGHT} />
        </S.ExpandButton>
      </S.HeaderRight>
    </S.ItemHeader>
  );
}
